import { useCallback, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { can } from "../../../../shared/auth/access";
import type { SessionUser } from "../../../../shared/auth/access";
import type { Rfq, Candidate } from "../../domain/rfq-types";
import { rfqDraftPayload } from "../../domain/rfq-draft";
import type { RfqDraftForm, RfqRequestSnapshot } from "../../domain/rfq-draft";
import { createRfqDraft, readRfqDraftCandidates, saveRfqSelection, changeRfqLifecycle } from "../../infrastructure/rfqs/rfq-draft-gateway";
import type { RfqLifecycleAction } from "../../infrastructure/rfqs/rfq-draft-gateway";
import { requestSnapshotHash } from "../../infrastructure/rfqs/request-snapshot-hash";
import { localDateTimeToUtc } from "../../rfq-timezone";
import type { ProcurementScope } from "../shared/useProcurementScope";
import { rfqUiMessage } from "./rfq-presentation";
import { useRfqReadScope } from "./useRfqReadScope";

type Props = {
  scope: ProcurementScope; snapshot: RfqRequestSnapshot; user: SessionUser; rfq: Rfq | null;
  selected: string[]; loading: boolean; error: string;
  setRfq: Dispatch<SetStateAction<Rfq | null>>; setCandidates: Dispatch<SetStateAction<Candidate[]>>;
  setError: Dispatch<SetStateAction<string>>;
};
const initialForm = (): RfqDraftForm => ({ method: "OPEN", deadlineAt: "", timezone: "Europe/Istanbul", reason: "" });
const writeError = (value: unknown, fallback: string) => {
  const code = value instanceof Error && "code" in value && typeof value.code === "string" ? value.code : "";
  return ["NETWORK_ERROR", "INVALID_API_RESPONSE"].includes(code) || /^HTTP_5/.test(code)
    ? "İşlemin sonucu doğrulanamadı. Tekrar kaydetmeden önce teklif talebini yenileyip durumunu kontrol edin."
    : rfqUiMessage(value, fallback);
};
export function useRfqDraftCommands({ scope, snapshot, user, rfq, selected, loading, error, setRfq, setCandidates, setError }: Props) {
  const recordScope = useRfqReadScope(scope, rfq?.id);
  const snapshotKey = JSON.stringify(snapshot);
  const selectedKey = JSON.stringify(selected);
  const latest = useRef({ rfq, snapshotKey, selectedKey });
  latest.current = { rfq, snapshotKey, selectedKey };
  const lock = useRef<{ scope: ProcurementScope; token: object } | null>(null);
  const createdScope = useRef<ProcurementScope | null>(null);
  const [state, setState] = useState({ scope, form: initialForm(), composeError: "", busy: "" });
  const visible = state.scope === scope ? state : { scope, form: initialForm(), composeError: "", busy: "" };
  const setForm: Dispatch<SetStateAction<RfqDraftForm>> = useCallback(value => {
    if (!scope.isCurrent()) return;
    setState(current => {
      const previous = current.scope === scope ? current : { scope, form: initialForm(), composeError: "", busy: "" };
      return { ...previous, form: typeof value === "function" ? value(previous.form) : value };
    });
  }, [scope]);
  const setComposeError: Dispatch<SetStateAction<string>> = useCallback(value => {
    if (!scope.isCurrent()) return;
    setState(current => {
      const previous = current.scope === scope ? current : { scope, form: initialForm(), composeError: "", busy: "" };
      return { ...previous, composeError: typeof value === "function" ? value(previous.composeError) : value };
    });
  }, [scope]);
  const start = (busy: string) => {
    if (!scope.isCurrent() || lock.current?.scope === scope) return null;
    const token = {};
    lock.current = { scope, token };
    setState(current => ({ ...(current.scope === scope ? current : { scope, form: initialForm(), composeError: "" }), busy }));
    return token;
  };
  const finish = (token: object) => {
    if (lock.current?.token !== token) return;
    lock.current = null;
    if (scope.isCurrent()) setState(current => ({ ...current, busy: "" }));
  };
  const create = async () => {
    if (loading || error || latest.current.rfq || createdScope.current === scope || !can(user, "rfq.manage")) return;
    const token = start("create");
    if (!token) return;
    setComposeError("");
    try {
      const form = visible.form;
      const deadlineAt = localDateTimeToUtc(form.deadlineAt, form.timezone);
      const specificationHash = await requestSnapshotHash(snapshot);
      if (!scope.isCurrent()) return;
      if (latest.current.snapshotKey !== snapshotKey) {
        setComposeError("Talep bilgileri değişti. Güncel bilgilerle yeniden deneyin.");
        return;
      }
      if (latest.current.rfq) return;
      const created = await createRfqDraft(rfqDraftPayload(snapshot, form, deadlineAt, specificationHash));
      if (!scope.isCurrent()) return;
      createdScope.current = scope;
      if (latest.current.rfq) return;
      if (latest.current.snapshotKey !== snapshotKey) {
        const message = "Taslak kaydedildi, ancak talep bilgileri değişti. Teklif talebini yenileyin; taslağı tekrar oluşturmayın.";
        setError(message);
        scope.notify(message);
        return;
      }
      latest.current.rfq = created;
      setRfq(created);
      setError("");
      scope.notify("Teklif Talebi taslağı oluşturuldu.");
      try {
        const candidates = await readRfqDraftCandidates(created.id);
        if (scope.isCurrent() && latest.current.rfq?.id === created.id) setCandidates(candidates);
      } catch {
        if (scope.isCurrent() && latest.current.rfq?.id === created.id) {
          const message = "Teklif Talebi taslağı oluşturuldu, ancak tedarikçi adayları yüklenemedi. Listeyi yenileyin; taslağı tekrar oluşturmayın.";
          setError(message);
          scope.notify(message);
        }
      }
    } catch (value) {
      if (scope.isCurrent()) {
        const message = writeError(value, "Teklif Talebi oluşturulamadı.");
        setComposeError(message);
        if (value instanceof Error && "code" in value) setError(message);
      }
    } finally { finish(token); }
  };
  const runRecord = async (action: "selection" | RfqLifecycleAction) => {
    const permission = action === "selection" ? "rfq.manage" : "rfq.publish";
    if (!rfq || !recordScope.isCurrent() || !can(user, permission) || latest.current.rfq?.id !== rfq.id || latest.current.rfq.status !== rfq.status || action === "selection" && latest.current.selectedKey !== selectedKey || (latest.current.rfq.current_version ?? latest.current.rfq.currentVersion) !== (rfq.current_version ?? rfq.currentVersion)) return;
    const token = start(action);
    if (!token) return;
    setError("");
    try {
      const result = action === "selection" ? await saveRfqSelection(rfq, selected) : await changeRfqLifecycle(rfq, action, visible.form.reason);
      if (!recordScope.isCurrent()) return;
      const current = latest.current.rfq;
      if (current && ((current.current_version ?? current.currentVersion) !== (rfq.current_version ?? rfq.currentVersion) || current.status !== rfq.status)) {
        const message = "İşlem sunucuda tamamlandı; ekran bu sırada güncellendi. Güncel durumu görmek için teklif talebini yenileyin.";
        setError(message);
        scope.notify(message);
        return;
      }
      latest.current.rfq = result;
      setRfq(result);
      scope.notify(action === "selection" ? "Tedarikçi seçimi sunucuda doğrulandı." : "Teklif Talebi işlemi tamamlandı.");
    } catch (value) {
      if (recordScope.isCurrent()) setError(writeError(value, action === "selection" ? "Tedarikçi seçimi kaydedilemedi." : "Teklif Talebi işlemi tamamlanamadı."));
    } finally { finish(token); }
  };
  return { form: visible.form, composeError: visible.composeError, submitting: visible.busy === "create", rfqCommandBusy: visible.busy !== "", setForm, setComposeError, create, saveSelection: () => runRecord("selection"), command: (action: RfqLifecycleAction) => runRecord(action) };
}
